import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Truck, Lock, Layers, Info } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PosterCanvas } from "@/components/PosterCanvas";
import { ColorField } from "@/components/ColorField";
import { FontSelect } from "@/components/FontSelect";
import { ChoiceGrid } from "@/components/configurator/ChoiceGrid";
import { PhotoStep } from "@/components/configurator/PhotoStep";
import { OrderDialog } from "@/components/configurator/OrderDialog";
import { TextStyleControls } from "@/components/configurator/TextStyleControls";
import { SliderField } from "@/components/SliderField";
import { getProduct, applyPreset, PRODUCTS } from "@/lib/products";
import { isShopifyEmbed, useEmbedViewport } from "@/lib/embed";
import { ProductThumbs } from "@/components/configurator/ProductThumbs";
import { draftKey, loadDraftCfg, useConfigDraft } from "@/lib/draft";
import { EventDateField } from "@/components/configurator/EventDateField";
import { TEMPLATES, SIZES, getTemplate, getSize, configFromTemplate, TEXT_FIELDS, FONT_GROUPS, DELIVERY_DELAY, PANEL_MATERIAL, NO_SUPPORT_NOTICE, FINAL_NOTICE, BISMILLAH_POSITIONS, templateFields, PHOTO_KEYS } from "@/lib/poster";

const BG_PRESETS = [
  { name: "Blanc Pur", hex: "#FFFFFF" }, { name: "Ivoire Doux", hex: "#FAF7F2" }, { name: "Sable Chaud", hex: "#F4EBE1" },
  { name: "Rose Poudré", hex: "#F7EBE8" }, { name: "Vert Émeraude", hex: "#1E3326" }, { name: "Bleu Nuit", hex: "#131C2E" },
  { name: "Noir Vesper", hex: "#1A1918" }, { name: "Bordeaux", hex: "#4A2223" },
];
const TEXT_PRESETS = [
  { name: "Or Impérial", hex: "#C5A059" }, { name: "Or Antique", hex: "#B38F46" }, { name: "Charbon Mat", hex: "#1C1917" },
  { name: "Blanc Pur", hex: "#FFFFFF" }, { name: "Bronze Cuivré", hex: "#8C5E3C" }, { name: "Sauge Ombrée", hex: "#4A5D4E" },
  { name: "Champagne", hex: "#E8D5B7" },
];

const Step = ({ value, n, title, children }) => (
  <AccordionItem value={value} className="border-[#DDD3C6]" data-testid={`step-${value}`}>
    <AccordionTrigger className="hover:no-underline py-5" data-testid={`step-${value}-trigger`}>
      <span className="flex items-baseline gap-4 text-left">
        <span className="font-mono text-xs text-[#7B5338]">0{n}</span>
        <span className="font-display text-2xl text-[#3A322B] font-normal">{title}</span>
      </span>
    </AccordionTrigger>
    <AccordionContent className="pb-6 pt-1 space-y-5">{children}</AccordionContent>
  </AccordionItem>
);

export default function Configurator() {
  const [params] = useSearchParams();
  const key = draftKey(params);
  const fromProduct = !!(params.get("produit") || params.get("titre") || params.get("modele"));
  const o = fromProduct ? 1 : 0;
  const [cfg, setCfg] = useState(() => {
    const draft = loadDraftCfg(key);
    if (draft?.templateId && draft.texts) return draft;
    const product = getProduct(params.get("produit"));
    if (product) return applyPreset(configFromTemplate(getTemplate(product.templateId)), product.preset);
    if (params.get("titre")) return applyPreset(configFromTemplate(getTemplate("bismillah-duo")), { texts: { names: params.get("titre") } });
    return configFromTemplate(getTemplate(params.get("modele")));
  });
  const [photos, setPhotos] = useState({ groom: null, bride: null });
  useConfigDraft(key, cfg, photos, setPhotos);
  const [orderOpen, setOrderOpen] = useState(false);
  const embed = isShopifyEmbed();
  const vp = useEmbedViewport(embed);
  const gridRef = useRef(null);
  const [view, setView] = useState(0);
  const gallery = embed && vp.images.length > 0;
  const panelRef = useRef(null);
  const embedPanel = () => {
    const lg = window.innerWidth >= 1024;
    const h = lg ? vp.vh - 32 : vp.vh * 0.7;
    if (!lg || !gridRef.current) return { height: h };
    const max = Math.max(0, gridRef.current.offsetHeight - h - 64);
    const y = Math.min(Math.max(vp.scroll - gridRef.current.offsetTop + 16, 0), max);
    return { height: h, transform: `translateY(${y}px)` };
  };
  const tpl = getTemplate(cfg.templateId);
  const size = getSize(cfg.sizeId);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setIn = (key, sub, v) => setCfg((c) => ({ ...c, [key]: { ...c[key], [sub]: v } }));
  const fields = useMemo(() => templateFields(tpl), [tpl]);
  const layouts = useMemo(() => Object.entries(tpl.layouts).map(([id, l]) => ({ id, label: l.label })), [tpl]);

  const order = () => {
    const needed = PHOTO_KEYS.slice(0, cfg.photoCount);
    if (needed.some((k) => !photos[k])) return toast.error(cfg.photoCount === 2 ? "Importez les deux photos avant de commander" : "Importez votre photo avant de commander");
    if (!cfg.eventDate) return toast.error("Indiquez la date de votre événement");
    if (!cfg.texts.names.trim()) return toast.error("Renseignez le ou les prénoms");
    setOrderOpen(true);
  };

  return (
    <div ref={gridRef} className="max-w-[1500px] mx-auto px-4 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
      <section ref={panelRef} style={embed ? embedPanel() : undefined} className={`lg:col-span-7 xl:col-span-7 ${embed ? "self-start will-change-transform" : "lg:sticky h-[70vh] lg:top-32 lg:h-[calc(100vh-9.5rem)]"} flex flex-col grad-panel rounded-2xl p-6 lg:p-10 border border-[#DDD3C6] shadow-inner`} data-testid="live-preview-panel">
        {gallery ? <>
          <div className="flex-1 min-h-0"><img src={vp.images[view] || vp.images[0]} alt="Photo du produit" className="w-full h-full object-contain rounded-xl" data-testid="product-gallery-main-image" /></div>
          {vp.images.length > 1 && <ProductThumbs images={vp.images} view={view} onSelect={setView} />}
        </> : <>
        <div className="flex items-center justify-between mb-4 text-xs uppercase tracking-[0.2em] text-[#9A8C7D]">
          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#8A9A86] animate-pulse" /> Aperçu en direct</span>
          <span data-testid="preview-size-label">{size.label} · {size.price} €</span>
        </div>
        <div className="flex-1 min-h-0"><PosterCanvas cfg={cfg} photos={photos} /></div>
        <p className="text-center text-xs text-[#9A8C7D] mt-4">Cet aperçu est généré par le même moteur que le fichier d'impression.</p>
        </>}
      </section>

      <section className="lg:col-span-5 xl:col-span-5" data-testid="configurator-controls">
        <h1 className="font-display text-4xl sm:text-5xl leading-tight text-gradient">Composez votre tableau</h1>
        <p className="text-[#6B5F53] mt-3 mb-6">Chaque choix s'applique instantanément à l'aperçu.</p>
        <EventDateField value={cfg.eventDate || ""} onChange={(v) => setCfg((c) => ({ ...c, eventDate: v, texts: { ...c.texts, number: v ? v.split("-").reverse().join(".") : c.texts.number } }))} />
        <Accordion type="multiple" defaultValue={["modele", "photo"]} className="w-full">
          {!fromProduct && <Step value="modele" n={1} title="Modèle">
            <div className="grid gap-3">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" onClick={() => setCfg((c) => configFromTemplate(t, c))} data-testid={`template-option-${t.id}`}
                  className={`choice !text-left flex items-center gap-4 ${cfg.templateId === t.id ? "choice-active" : ""}`}>
                  <span className="w-10 h-14 rounded-sm border border-[#DDD3C6] shrink-0" style={{ background: t.defaults.bgColor, boxShadow: `inset 0 0 0 3px ${t.defaults.bgColor}, inset 0 0 0 4px ${t.defaults.textColor}` }} />
                  <span><span className="block font-display text-xl tracking-[0.12em]">{t.name}</span><span className="block text-xs text-[#9A8C7D] mt-1">{t.tagline}</span></span>
                </button>
              ))}
            </div>
          </Step>}
          <Step value="format" n={2 - o} title="Format">
            <ChoiceGrid cols={2} testPrefix="size-option" value={cfg.sizeId} onChange={(v) => set({ sizeId: v })}
              options={SIZES.map((s) => ({ id: s.id, label: s.label, sub: `${s.price} € · Forex` }))} />
            <p className="text-xs text-[#9A8C7D]">{PANEL_MATERIAL}. {NO_SUPPORT_NOTICE}</p>
          </Step>
          <Step value="photo" n={3 - o} title="Vos photos">
            <PhotoStep photos={photos} setPhotos={setPhotos} cfg={cfg} set={set} />
            <div className="space-y-2"><span className="label-xs">Disposition des photos</span>
              <ChoiceGrid cols={2} testPrefix="layout-option" value={cfg.layoutId} onChange={(v) => set({ layoutId: v })} options={layouts} /></div>
          </Step>
          <Step value="couleurs" n={4 - o} title="Couleurs">
            <ColorField label="Fond de la toile" value={cfg.bgColor} onChange={(v) => set({ bgColor: v })} presets={BG_PRESETS} testPrefix="bg-color" />
            <div className="h-px bg-[#DDD3C6]" />
            <ColorField label="Écritures & ornements" value={cfg.textColor} onChange={(v) => set({ textColor: v })} presets={TEXT_PRESETS} testPrefix="text-color" />
          </Step>
          <Step value="bismillah" n={5 - o} title="Bismillah">
            <div className="flex gap-4 items-center" data-testid="bismillah-block">
              <img src="/bismillah.png" alt="Bismillah" className="h-10 opacity-80" />
              <p className="text-xs text-[#9A8C7D]">« Au nom d'Allah, le Tout Miséricordieux, le Très Miséricordieux »</p>
            </div>
            <div className="space-y-2"><span className="label-xs">Intégrer au tableau</span>
              <ChoiceGrid cols={2} testPrefix="bismillah-enabled" value={cfg.bismillah.enabled ? "oui" : "non"} onChange={(v) => setIn("bismillah", "enabled", v === "oui")}
                options={[{ id: "oui", label: "Oui" }, { id: "non", label: "Non" }]} /></div>
            {cfg.bismillah.enabled && (<>
              <div className="space-y-2"><span className="label-xs">Emplacement</span>
                <ChoiceGrid cols={2} testPrefix="bismillah-position" value={cfg.bismillah.position} onChange={(v) => setIn("bismillah", "position", v)} options={BISMILLAH_POSITIONS} /></div>
              <ColorField label="Couleur de la Bismillah" value={cfg.bismillah.color} onChange={(v) => setIn("bismillah", "color", v)} presets={TEXT_PRESETS} testPrefix="bismillah-color" />
              <SliderField label="Taille de la Bismillah" value={cfg.bismillah.size ?? 100} min={30} max={200} onChange={(v) => setIn("bismillah", "size", v)} testId="bismillah-size" />
              <SliderField label="Position (haut / bas)" value={cfg.bismillah.dy ?? 0} min={-30} max={30} step={0.5} format={(v) => `${v > 0 ? "+" : ""}${v} %`} onChange={(v) => setIn("bismillah", "dy", v)} testId="bismillah-offset" />
            </>)}
          </Step>
          <Step value="textes" n={6 - o} title="Textes">
            {fields.map((f) => (
              <div key={f.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="label-xs">{f.label}</span>
                  <div className="flex rounded-full bg-[#EADFD3] p-0.5 text-xs" data-testid={`italic-toggle-${f.key}`}>
                    {[[false, "Normal"], [true, "Italique"]].map(([v, l]) => (
                      <button key={l} type="button" onClick={() => setIn("italic", f.key, v)} data-testid={`italic-toggle-${f.key}-${v ? "italic" : "normal"}`}
                        className={`px-3 py-1 rounded-full transition-colors duration-200 ${cfg.italic[f.key] === v ? "bg-white text-[#3A322B] shadow-sm" : "text-[#9A8C7D]"} ${v ? "italic" : ""}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {f.multiline ? (
                  <Textarea rows={2} value={cfg.texts[f.key]} onChange={(e) => setIn("texts", f.key, e.target.value)} className="bg-white rounded-xl border-[#CDC0B0]" data-testid={`text-input-${f.key}`} />
                ) : (
                  <Input dir="auto" value={cfg.texts[f.key]} placeholder={f.placeholder} onChange={(e) => setIn("texts", f.key, e.target.value)} className="h-12 bg-white rounded-xl border-[#CDC0B0]" data-testid={`text-input-${f.key}`} />
                )}
                <TextStyleControls fieldKey={f.key} style={cfg.textStyles?.[f.key]} onChange={(v) => setIn("textStyles", f.key, v)} />
              </div>
            ))}
            <p className="text-xs text-[#9A8C7D]">Laissez un champ vide pour masquer l'élément. Taille, position (en % de la hauteur du tableau) et alignement se règlent sous chaque texte.</p>
          </Step>
          <Step value="polices" n={7 - o} title="Typographies">
            {FONT_GROUPS.filter((g) => fields.some((f) => f.group === g.key)).map((g) => (
              <FontSelect key={g.key} label={g.label} value={cfg.fonts[g.key]} onChange={(v) => setIn("fonts", g.key, v)} testId={`font-select-${g.key}`}
                sample={TEXT_FIELDS.filter((f) => f.group === g.key).map((f) => cfg.texts[f.key]).find((t) => t?.trim())}
                italic={TEXT_FIELDS.some((f) => f.group === g.key && cfg.italic[f.key])} />
            ))}
          </Step>
        </Accordion>

        <div className="mt-10 rounded-2xl grad-card border border-[#C2B4A2]/70 p-6 space-y-4" data-testid="final-preview-section">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl uppercase tracking-[0.12em]">Aperçu final</h2>
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6B5F53]"><span className="w-2 h-2 rounded-full bg-[#8A9A86] animate-pulse" /> En direct</span>
          </div>
          <div className="h-[560px] lg:h-[640px]"><PosterCanvas cfg={cfg} photos={photos} testId="final-preview-canvas" /></div>
          <p className="text-center text-xs text-[#6B5F53]">Voici exactement le tableau qui sera imprimé · {size.label} · {PANEL_MATERIAL}</p>
          <p className="flex items-start gap-2 text-xs text-[#7B5338] bg-white/60 rounded-xl p-3" data-testid="final-notice"><Lock size={14} className="shrink-0 mt-0.5" /> {FINAL_NOTICE}</p>
        </div>
        <div className="mt-8 rounded-2xl bg-white border border-[#DDD3C6] p-6 space-y-4" data-testid="order-summary">
          <div className="flex justify-between items-baseline">
            <span className="text-[#6B5F53]">{tpl.name} · {size.label}</span>
            <span className="font-display text-3xl" data-testid="order-summary-price">{size.price} €</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#3A322B] bg-[#EADFD3] rounded-xl p-3" data-testid="delivery-delay-notice">
            <Truck size={16} className="text-[#7B5338]" /> Délai de livraison : <strong>{DELIVERY_DELAY}</strong>
          </div>
          <div className="flex justify-between text-sm" data-testid="free-shipping-notice">
            <span className="text-[#6B5F53]">Livraison</span><strong className="text-[#4A5D4E] uppercase tracking-wider text-xs">Offerte</strong>
          </div>
          <ul className="text-xs text-[#6B5F53] space-y-1.5" data-testid="product-notes">
            <li className="flex gap-2"><Layers size={14} className="text-[#7B5338] shrink-0" /> {PANEL_MATERIAL} — {size.label}</li>
            <li className="flex gap-2"><Info size={14} className="text-[#7B5338] shrink-0" /> {NO_SUPPORT_NOTICE}</li>
            <li className="flex gap-2"><Lock size={14} className="text-[#7B5338] shrink-0" /> {FINAL_NOTICE}</li>
          </ul>
          <button type="button" onClick={order} className="btn-gold w-full justify-center" data-testid="order-open-button">Ajouter au panier</button>
        </div>
      </section>
      <OrderDialog open={orderOpen} onOpenChange={setOrderOpen} cfg={cfg} photos={photos} embed={embed} draft={key}
        productHandle={params.get("produit") || (cfg.templateId === "fiancailles" ? PRODUCTS.find((p) => p.templateId === "fiancailles")?.id : undefined)} />
    </div>
  );
}
